import * as Sentry from '@sentry/angular';
import { BrowserTracing } from '@sentry/tracing';
import { Injectable } from '@angular/core';
import { EnvService } from './env.service';
import { StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import produce from 'immer';
import { get, hasIn } from 'lodash-es';
import { CcsSentryError, mapErrorToCcsSentryError, mapHttpErrorToCcsResponseError } from '../store/utils';
import { HttpErrorResponse } from '@angular/common/http';
import { UserRole } from '../models';

const project = require('package.json');

const IGNORED_ERROR_REGEX_STRINGS = [
  // General JS errors
  'Uncaught \\(in promise\\): Timeout',
  'ResizeObserver loop limit exceeded',

  // Registration
  'Patient already migrated',
  'Patient already registered',
  'patient is already registered in user table',
  'patient not found',
  'No patient found matching insurance number',
  'No patient found matching patient data provided',
  'Patient primary email does not match the email provided',
  'value larger than specified precision allowed',

  // PPP errors
  'Decline - Expired card',
  'Decline - Invalid Card Verification Number',
  'The credit card number is invalid',
  'Decline - Invalid account number',
  'it did not pass the card verification number',
  'Decline - Inactive card or card not authorized',

  // Password Changes
  'Password cannot be your current password',
  'Old Password is not correct',
];

interface StoreUtilsOptions {
  ctx: StateContext<any>;
  stateKey: string | null;
  error?: CcsSentryError | HttpErrorResponse | Error | string;
  errorStateKey?: string;
  updating?: boolean;
  updateStateFunction?: (draft: any) => void;
}

interface HandleErrorOptions extends StoreUtilsOptions {
  resetData?: boolean;
  skipRecordingError?: boolean;
}

interface HandleSuccessOptions extends StoreUtilsOptions {
  data?: any;
}

function addDataCyAttribute(breadcrumb: Sentry.Breadcrumb, hint: Sentry.BreadcrumbHint) {
  let target = hint['event']['target'] as HTMLElement;
  while (target && !target.hasAttribute('data-cy') && target.parentElement) {
    target = target.parentElement;
  }
  if (target && target.hasAttribute('data-cy')) {
    const dataCyPrefix = `${target.tagName.toLowerCase()}[data-cy='${target.getAttribute('data-cy')}']`;
    breadcrumb.message = `${dataCyPrefix} ${breadcrumb.message}`;
  }
}

function recursivelyScrubKeys(data: { [key: string]: any } | any[], scrubKeys: string[]) {
  if (!data) {
    return;
  } else if (Array.isArray(data)) {
    for (let element of data) {
      if (typeof element === 'object') {
        recursivelyScrubKeys(element, scrubKeys);
      }
    }
  } else if (typeof data === 'object') {
    for (const key of Object.keys(data)) {
      if (typeof data[key] === 'object') {
        recursivelyScrubKeys(data[key], scrubKeys);
      } else if (scrubKeys.includes(key.toLowerCase())) {
        data[key] = '***';
      }
    }
  }
}

@Injectable({ providedIn: 'root' })
export class SentryService {
  enabled: boolean = false;
  userRole: UserRole | null = null;

  constructor(private envService: EnvService) {
    this.enabled = this.envService.sentry.enabled;
  }

  init() {
    if (!this.enabled) {
      return;
    }

    const SCRUB_KEYS = this.envService.sentry.scrubKeys.map((k) => k.toLowerCase());
    const self = this;

    Sentry.init({
      dsn: this.envService.sentry.dsn,
      tracesSampleRate: 0.0,
      environment: this.envService.environment,
      release: project.version,
      normalizeDepth: this.envService.sentry.normalizeDepth,
      integrations: [
        new BrowserTracing({
          tracingOrigins: ['localhost'],
          routingInstrumentation: Sentry.routingInstrumentation,
        }),
      ],
      beforeBreadcrumb(breadcrumb, hint) {
        if (breadcrumb.category?.startsWith('ui') && hint) {
          addDataCyAttribute(breadcrumb, hint);
        }

        return breadcrumb;
      },
      beforeSend(event) {
        if (self.isSentryEventIgnored(event)) {
          return null;
        }

        if (event.breadcrumbs) {
          event.breadcrumbs.forEach((breadcrumb) => {
            if (breadcrumb.data) {
              recursivelyScrubKeys(breadcrumb.data, SCRUB_KEYS);
            }
          });
        }

        return event;
      },
    });

    Sentry.setTag('adblock_detected', (window as any).adBlockEnabled ? 'Yes' : 'No');
  }

  recordUser(patientId: number) {
    if (!this.enabled) {
      return;
    }

    Sentry.setUser({ id: patientId.toString() });
  }

  setUserRole(role: UserRole | null) {
    this.userRole = role;
  }

  static _recordError(error: CcsSentryError) {
    Sentry.setExtra('error', { ...error });
    const eventId = Sentry.captureException(error);
    error.sentryEventId = eventId;
  }

  recordError(error: CcsSentryError) {
    if (!this.enabled) {
      return;
    }

    SentryService._recordError(error);
  }

  addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
    if (!this.enabled) {
      return;
    }

    Sentry.addBreadcrumb(breadcrumb);
  }

  static _handleHelper(options: StoreUtilsOptions, objToModify: any, skipRecordingError?: boolean): void {
    let { error, errorStateKey, updating } = options;
    const loadingStateKey = 'isLoading';
    const updatingStateKey = 'isUpdating';
    const completeStateKey = 'isFinished';
    errorStateKey ||= 'error';

    if (
      errorStateKey in objToModify &&
      loadingStateKey in objToModify &&
      (!updating || updatingStateKey in objToModify)
    ) {
      objToModify[loadingStateKey] = false;
      if (updating) {
        objToModify[updatingStateKey] = false;
      }

      if (!error) {
        objToModify[errorStateKey] = null;
      } else if (error instanceof CcsSentryError) {
        objToModify[errorStateKey] = error;
        if (!skipRecordingError) {
          SentryService._recordError(error);
        }
      } else if (error instanceof HttpErrorResponse) {
        const sentryError = mapHttpErrorToCcsResponseError(error);
        objToModify[errorStateKey] = sentryError;
        if (!skipRecordingError) {
          SentryService._recordError(sentryError);
        }
      } else if (error instanceof Error) {
        const sentryError = mapErrorToCcsSentryError(error);
        objToModify[errorStateKey] = sentryError;
        if (!skipRecordingError) {
          SentryService._recordError(sentryError);
        }
      } else {
        const sentryError = new CcsSentryError(error);
        objToModify[errorStateKey] = sentryError;
        if (!skipRecordingError) {
          SentryService._recordError(sentryError);
        }
      }

      if (completeStateKey in objToModify) {
        objToModify[completeStateKey] = true;
      }
    } else {
      console.error('Draft does not have errorStateKey, loadingStateKey, and/or updatingStateKey properties!');
    }
  }

  handleError(options: HandleErrorOptions): Observable<null> {
    const { ctx, stateKey, skipRecordingError } = options;
    const resetData = options.resetData === undefined ? true : options.resetData;

    ctx.setState(
      produce((draft: any) => {
        if (!stateKey || hasIn(draft, stateKey)) {
          const objToModify = !!stateKey ? get(draft, stateKey) : draft;

          SentryService._handleHelper(options as StoreUtilsOptions, objToModify, skipRecordingError);

          if (resetData && 'data' in objToModify) {
            objToModify.data = objToModify.data instanceof Array ? [] : null;
          } else if (resetData) {
            console.error('data not in draft object');
          }
        } else if (!!stateKey) {
          console.error('Draft does not exist or does not have stateKey property!');
        }

        if (options.updateStateFunction) options.updateStateFunction(draft);
      })
    );

    return of(null);
  }

  handleSuccess(options: HandleSuccessOptions): Observable<null> {
    const { ctx, stateKey, data } = options;

    ctx.setState(
      produce((draft: any) => {
        if (!stateKey || hasIn(draft, stateKey)) {
          const objToModify = !!stateKey ? get(draft, stateKey) : draft;

          SentryService._handleHelper(options as StoreUtilsOptions, objToModify);

          if (data !== undefined && 'data' in objToModify) {
            objToModify.data = data;
          } else if (data !== undefined) {
            console.error('data not in draft object');
          }

          if (options.updateStateFunction) options.updateStateFunction(draft);
        } else if (!!stateKey) {
          console.error('Draft does not exist or does not have stateKey property!');
        }
      })
    );

    return of(null);
  }

  private isSentryEventIgnored(sentryEvent: Sentry.Event) {
    const isBlackListed = Boolean(
      sentryEvent.exception?.values?.some(
        ({ value }) =>
          !!value &&
          IGNORED_ERROR_REGEX_STRINGS.some((regexString) => {
            const regex = new RegExp(regexString, 'i');
            return regex.test(value);
          })
      )
    );

    if (isBlackListed) return true;

    const isUnauthorizedAgentError =
      this.userRole === UserRole.Agent &&
      sentryEvent.exception?.values?.some(({ value }) => !!value && value.startsWith('403'));

    return isUnauthorizedAgentError;
  }
}
